import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Cloud,
  Radio,
  Layers,
  Sparkles,
  Cpu,
  Tv2,
  Globe2,
  ShieldCheck,
  Shield,
  ArrowRight,
  CircleDot,
  Terminal,
  Waves,
  Wifi,
  Zap,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const HERO_BG = "https://images.unsplash.com/photo-1768222935380-0a3a76fbb42e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwzfHxicm9hZGNhc3QlMjBjb250cm9sJTIwcm9vbXxlbnwwfHx8fDE3ODMzNzA3MzV8MA&ixlib=rb-4.1.0&q=85";
const INFRA_BG = "https://images.unsplash.com/photo-1780845055221-cbe31f2f6774?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwyfHxzZXJ2ZXIlMjByYWNrcyUyMGRhcmt8ZW58MHx8fHwxNzgzMzcwNzM1fDA&ixlib=rb-4.1.0&q=85";
const NET_BG = "https://images.pexels.com/photos/14314638/pexels-photo-14314638.jpeg";
const EDIT_BG = "https://images.pexels.com/photos/8088373/pexels-photo-8088373.jpeg";

const PROTOCOLS = ["UDP", "HLS", "SRT", "RTP", "RTMP", "WebRTC", "NDI", "SDI", "ASI"];

const FEATURES = [
  { icon: Radio, title: "9-protocol I/O", body: "Ingest and deliver over UDP, HLS, SRT, RTP, RTMP, WebRTC, NDI, SDI, ASI — one console." },
  { icon: Tv2, title: "Up to 4K resolution", body: "GPU-accelerated NVENC pipelines built for HD, FHD, and 2160p FAST channels." },
  { icon: CircleDot, title: "SCTE-35 ingest & insert", body: "Frame-accurate ad markers with IDR alignment. DATERANGE conformance out of the box." },
  { icon: Sparkles, title: "AI break detection", body: "Automatic break-point proposal on ingest. Operator confirms with one keystroke." },
  { icon: Cpu, title: "AI metadata generation", body: "Title, synopsis, genres, and EPG descriptions authored by Claude Sonnet 4.6." },
  { icon: Layers, title: "14-layer graphics editor", body: "Logo, ticker, lower-third, HTML, poll — layered, scheduled, and driven by CEF." },
  { icon: Globe2, title: "OTT delivery + EPG", body: "HLS with XMLTV EPG. One-click packaging for every FAST platform onboarding." },
  { icon: ShieldCheck, title: "White-label ready", body: "Per-tenant branding, isolated engines, RBAC. Ship it as your own product tomorrow." },
];

const BOOK_DEMO_URL = "mailto:contact@push2stream.com?subject=MOXYPLAY%20demo%20request";

export default function Landing() {
  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      {/* Nav */}
      <nav className="fixed top-0 inset-x-0 z-50 border-b border-white/5 bg-background/80 backdrop-blur-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between h-16 px-6">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
            <div className="h-9 w-9 rounded-lg overflow-hidden bg-[#060A1F] p-1 flex items-center justify-center" style={{ boxShadow: "0 0 14px rgba(139,92,246,0.35)" }}>
              <img
                src="https://customer-assets-jt897jd0.emergentagent.net/job_broadcast-cloud-2/artifacts/jeb6uhlp_FullLogo_Transparent_NoBuffer.png"
                alt="MOXYPLAY"
                className="w-full h-full object-contain"
              />
            </div>
            <span className="font-display font-black tracking-tight text-lg text-gradient">MOXYPLAY</span>
          </Link>
          <div className="hidden md:flex items-center gap-8 text-sm text-zinc-400">
            <a href="#features" className="hover:text-white transition-colors" data-testid="nav-features">Features</a>
            <a href="#protocols" className="hover:text-white transition-colors" data-testid="nav-protocols">Protocols</a>
            <Link to="/pricing" className="hover:text-white transition-colors" data-testid="nav-pricing">Pricing</Link>
            <Link to="/hardware" className="hover:text-white transition-colors" data-testid="nav-hardware">Hardware</Link>
            <a href="#contact" className="hover:text-white transition-colors" data-testid="nav-contact">Contact</a>
          </div>
          <a href={BOOK_DEMO_URL} data-testid="nav-signup-btn">
            <Button className="tenant-btn rounded-full px-4" size="sm">
              Book demo <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </a>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-32 pb-24 px-6 grain overflow-hidden brand-hero-bg">
        <div className="absolute inset-0 -z-10 dotted-grid opacity-40" />
        <div className="max-w-7xl mx-auto relative">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 gradient-stroke rounded-full px-3 py-1 text-[10px] uppercase tracking-widest font-mono text-zinc-300 mb-6">
              <span className="dot-live" /> Interactive FAST channel playout · Cloud + On-Prem
            </div>
            <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] max-w-5xl">
              Streamline your broadcast, OTT & <span className="text-gradient">ad&nbsp;insertion</span> operations.
            </h1>
            <p className="mt-6 text-lg text-zinc-400 max-w-2xl leading-relaxed">
              Cloud-native and on-premises playout with 9-protocol I/O, SCTE-35, AI-driven break detection and
              metadata, a 14-layer graphics compositor, and EPG for every OTT platform — controlled from your
              browser.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href={BOOK_DEMO_URL} data-testid="hero-signup-btn">
                <Button className="tenant-btn rounded-full px-6 h-11" size="lg">
                  Book demo <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
              <Link to="/login" data-testid="hero-signin-btn">
                <Button variant="outline" className="tenant-outline rounded-full h-11 border-0">
                  Sign in
                </Button>
              </Link>
            </div>

            {/* Live status bar */}
            <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-4xl">
              {[
                ["CHANNELS", "12"],
                ["UPTIME", "99.997%"],
                ["END-TO-END LATENCY", "< 1.8s"],
                ["MAX RESOLUTION", "3840×2160"],
              ].map(([k, v]) => (
                <div key={k} className="panel px-3 py-2.5">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{k}</div>
                  <div className="font-display font-bold text-2xl mt-1">{v}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Protocols */}
      <section id="protocols" className="py-20 px-6 border-t border-zinc-900">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-baseline justify-between mb-8">
            <div>
              <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">§ 01 — I/O Matrix</p>
              <h2 className="font-display font-bold text-3xl mt-1">Every protocol you actually ship on.</h2>
            </div>
            <Wifi className="w-8 h-8 text-zinc-700" />
          </div>
          <div className="grid grid-cols-3 md:grid-cols-9 gap-2">
            {PROTOCOLS.map((p, i) => (
              <div
                key={p}
                data-testid={`protocol-${p.toLowerCase()}`}
                className="panel px-3 py-4 text-center hover:border-zinc-600 transition-colors"
              >
                <div className="text-[10px] font-mono text-zinc-500">{String(i + 1).padStart(2, "0")}</div>
                <div className="font-display font-black text-lg mt-1">{p}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features grid */}
      <section id="features" className="py-24 px-6 border-t border-zinc-900">
        <div className="max-w-7xl mx-auto">
          <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">§ 02 — Capabilities</p>
          <h2 className="font-display font-bold text-4xl mt-1 mb-12">Built for broadcast operators. Priced for OTT startups.</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {FEATURES.map((f) => (
              <div key={f.title} className="panel p-5 hover-highlight" data-testid={`feature-${f.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <f.icon className="w-6 h-6 text-red-500 mb-4" strokeWidth={1.5} />
                <div className="font-display font-bold text-lg">{f.title}</div>
                <div className="text-sm text-zinc-400 mt-2 leading-relaxed">{f.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Split feature: infra */}
      <section className="py-24 px-6 border-t border-zinc-900">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-8 items-center">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">§ 03 — Deployment</p>
            <h2 className="font-display font-bold text-4xl mt-1">Cloud, on-prem, or hybrid. One control plane.</h2>
            <p className="text-zinc-400 mt-4 leading-relaxed">
              Deploy the same playout engine on AWS, on your own bare-metal, or a mix of both. One control plane, one
              scheduler, one graphics engine — regardless of where the pixels are pushed.
            </p>
            <ul className="mt-6 space-y-3 text-sm">
              {[
                "Terraform-managed AWS EKS deploys with NVENC-enabled g4dn/g5 nodes",
                "GStreamer 1.24+ house pipeline with NVDEC/NVENC hardware codecs",
                "Warm-standby failover engine per channel with sub-second cutover",
                "Immutable append-only as-run logs with PostgreSQL and S3 archive",
              ].map((t) => (
                <li key={t} className="flex gap-3">
                  <CircleDot className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                  <span className="text-zinc-300">{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="relative h-96 rounded-sm overflow-hidden border border-zinc-800">
            <img src={INFRA_BG} alt="server infra" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-tr from-[#09090B] via-transparent to-transparent" />
            <div className="absolute bottom-4 left-4 font-mono text-xs text-zinc-400">
              <div>NODE-01 · g4dn.4xl · NVENC × 2</div>
              <div>NODE-02 · g5.2xl · NVENC × 1</div>
              <div className="text-emerald-400 mt-1">● HEALTHY · 42 CHANNELS ON AIR</div>
            </div>
          </div>
        </div>
      </section>

      {/* AI showcase */}
      <section className="py-24 px-6 border-t border-zinc-900">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-8 items-center">
          <div className="relative h-96 rounded-sm overflow-hidden border border-zinc-800 order-2 md:order-1">
            <img src={EDIT_BG} alt="edit suite" className="w-full h-full object-cover opacity-90" />
            <div className="absolute inset-0 bg-gradient-to-br from-[#09090B]/70 via-transparent to-[#09090B]/70" />
            <div className="absolute inset-x-0 bottom-0 p-4 font-mono text-xs">
              <div className="text-zinc-400">AI BREAK PROPOSAL · 00:22:18.400</div>
              <div className="text-red-400">confidence 0.94 · confirm to insert SCTE-35</div>
            </div>
          </div>
          <div className="order-1 md:order-2">
            <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">§ 04 — Applied AI</p>
            <h2 className="font-display font-bold text-4xl mt-1">Ship metadata and breaks 20× faster.</h2>
            <p className="text-zinc-400 mt-4 leading-relaxed">
              On ingest, we detect black frames and silence to propose ad opportunities, then call Claude Sonnet
              and Gemini for a title, synopsis, genre tags, and EPG description. Operators review — they don&apos;t
              write it.
            </p>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24 px-6 border-t border-zinc-900">
        <div className="max-w-7xl mx-auto">
          <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">§ 05 — Pricing</p>
          <h2 className="font-display font-bold text-4xl mt-1 mb-12">Predictable per-channel pricing.</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { name: "Starter", price: "$499", per: "/channel / mo", features: ["HD 1080p", "HLS + SRT out", "4-layer graphics", "Basic EPG", "Community support"] },
              { name: "Pro", price: "$1,499", per: "/channel / mo", features: ["Up to 4K", "9-protocol I/O", "14-layer graphics", "AI break + metadata", "SCTE-35", "24/7 support"], featured: true },
              { name: "Enterprise", price: "Contact", per: "custom", features: ["On-prem + Cloud", "Multi-tenant white-label", "Warm standby failover", "Dedicated engineer", "SLA 99.99%"] },
            ].map((tier) => (
              <div
                key={tier.name}
                className={`panel p-6 ${tier.featured ? "border-red-500/60" : ""}`}
                data-testid={`pricing-${tier.name.toLowerCase()}`}
              >
                {tier.featured && (
                  <div className="text-[10px] font-mono uppercase tracking-widest text-red-500 mb-3">
                    Most operators pick this
                  </div>
                )}
                <div className="font-display font-bold text-2xl">{tier.name}</div>
                <div className="mt-3">
                  <span className="font-display font-black text-4xl">{tier.price}</span>
                  <span className="text-zinc-500 text-sm ml-1">{tier.per}</span>
                </div>
                <ul className="mt-6 space-y-2 text-sm text-zinc-400">
                  {tier.features.map((f) => (
                    <li key={f} className="flex gap-2">
                      <CircleDot className="w-3 h-3 text-red-500 mt-1 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <a href={BOOK_DEMO_URL}>
                  <Button
                    className={`w-full mt-6 rounded-sm ${tier.featured ? "tenant-btn" : "bg-zinc-800 hover:bg-zinc-700"}`}
                    data-testid={`pricing-cta-${tier.name.toLowerCase()}`}
                  >
                    {tier.name === "Enterprise" ? "Talk to sales" : "Book demo"}
                  </Button>
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Six-icon "Why MOXYPLAY" strip (janya anchor) */}
      <section className="py-16 px-6 border-t border-white/5" data-testid="why-strip">
        <div className="max-w-7xl mx-auto">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">§ Why MOXYPLAY</div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tighter mb-8">Cloud playout that scales with your bouquet.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              { icon: Cloud, title: "Truly agile cloud playout", desc: "24×7 linear channels for OTT + broadcast, spun up in minutes." },
              { icon: Zap, title: "Browser-native UI", desc: "Fully virtual, drag-drop, deep-customisable — zero desktop apps." },
              { icon: Sparkles, title: "AI-driven automation", desc: "Automated scheduling, SCTE-35 insertion and monitoring." },
              { icon: Layers, title: "14-layer graphics compositor", desc: "Multi-layered dynamic template compositor with APNG library." },
              { icon: Shield, title: "Multi-channel switching", desc: "Live-event control across tens or hundreds of channels." },
              { icon: Users, title: "24×7 support", desc: "Dedicated operations team on-call for live production." },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="gradient-stroke p-5 rounded-xl">
                <Icon className="w-5 h-5 mb-3 text-cyan-300" strokeWidth={1.5} />
                <div className="font-display font-bold text-base leading-tight">{title}</div>
                <div className="text-xs text-zinc-400 mt-1.5 leading-relaxed">{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3-pillar Products (wisedv anchor) */}
      <section className="py-20 px-6 border-t border-white/5" data-testid="pillars-strip">
        <div className="max-w-7xl mx-auto">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">§ The stack</div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tighter mb-10">One control plane — three production pillars.</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { icon: Radio, title: "Channel Origination", desc: "Automated playlist, live ingest, 14-layer graphics, SCTE-35 insertion.", tag: "Playout" },
              { icon: Tv2, title: "Live & VOD Ad Insertion", desc: "SSAI on SCTE-35 triggers · CSAI · dynamic creative substitution.", tag: "Monetize" },
              { icon: Globe2, title: "Content Distribution", desc: "HLS + XMLTV EPG for Samsung TV+, Roku, Fire TV, Google TV, Web, STB.", tag: "Reach" },
            ].map(({ icon: Icon, title, desc, tag }) => (
              <div key={title} className="panel-hero rounded-2xl p-6 flex flex-col">
                <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-300">{tag}</div>
                <Icon className="w-8 h-8 my-4 text-fuchsia-300" strokeWidth={1.4} />
                <div className="font-display font-bold text-xl leading-tight">{title}</div>
                <div className="text-sm text-zinc-400 mt-2 leading-relaxed flex-1">{desc}</div>
                <div className="mt-4 text-[10px] font-mono uppercase tracking-widest text-cyan-300">Learn more →</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI callouts strip (wisedv anchor) */}
      <section className="py-16 px-6 border-t border-white/5" data-testid="ai-callouts">
        <div className="max-w-7xl mx-auto text-center">
          <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-300 mb-2">§ Applied AI</div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tighter max-w-3xl mx-auto">
            <span className="text-gradient">AI simplifies broadcasting</span>, enhances effectiveness, increases productivity.
          </h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-3">
            {[
              "AI-based Breakpoint Detection",
              "AI-based Metadata Enrichment",
              "AI-based Live Closed Captions",
              "AI-based Playout Scheduler",
            ].map((s) => (
              <div key={s} className="gradient-stroke rounded-xl p-4 text-sm font-display font-bold">{s}</div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners grid (janya anchor) */}
      <section className="py-16 px-6 border-t border-white/5" data-testid="partners-grid">
        <div className="max-w-7xl mx-auto">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2 text-center">§ Cloud & hardware partners</div>
          <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tighter mb-8 text-center">Certified everywhere it counts.</h2>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
            {["AWS", "Azure", "Google Cloud", "BlackMagic", "NVIDIA", "Cloudflare"].map((p) => (
              <div key={p} className="panel rounded-xl h-20 flex items-center justify-center font-display font-bold text-lg text-zinc-500 hover:text-zinc-100 transition-colors" data-testid={`partner-${p.toLowerCase().replace(/\s+/g,'-')}`}>{p}</div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter (wisedv anchor) */}
      <section className="py-16 px-6 border-t border-white/5" data-testid="newsletter">
        <div className="max-w-3xl mx-auto text-center panel rounded-2xl p-8">
          <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-300 mb-2">§ Stay in the loop</div>
          <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tighter">MOXYPLAY newsletter</h2>
          <p className="text-sm text-zinc-400 mt-2">Monthly release notes, broadcast tech deep-dives and early access to NAB / IBC previews.</p>
          <form className="mt-5 flex gap-2 max-w-lg mx-auto" onSubmit={(e) => { e.preventDefault(); }}>
            <input type="email" placeholder="you@studio.tv" required data-testid="newsletter-email" className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2 text-sm outline-none focus:border-cyan-400/60" />
            <button type="submit" data-testid="newsletter-submit" className="tenant-btn rounded-full px-5 py-2 font-mono text-[11px] uppercase tracking-widest">Subscribe</button>
          </form>
        </div>
      </section>

      {/* Solutions by industry (janya anchor) */}
      <section className="py-16 px-6 border-t border-white/5" data-testid="solutions-strip">
        <div className="max-w-7xl mx-auto">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">§ Solutions</div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tighter mb-8">Built for every vertical.</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              { label: "Sports", tag: "24×7 live" },
              { label: "Live Events", tag: "Multi-cam" },
              { label: "Spiritual", tag: "Community" },
              { label: "Entertainment", tag: "FAST bouquet" },
              { label: "News", tag: "Bulletin loop" },
            ].map((s) => (
              <div key={s.label} className="gradient-stroke rounded-xl p-4 flex flex-col justify-between min-h-[110px]" data-testid={`solution-${s.label.toLowerCase().replace(/\s+/g,'-')}`}>
                <div className="font-display font-bold text-lg">{s.label}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-300 mt-4">{s.tag}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Customer marquee (wisedv anchor) */}
      <section className="py-14 border-t border-white/5 overflow-hidden" data-testid="customer-marquee">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-6 text-center">§ Trusted by broadcasters worldwide</div>
        <div className="marquee">
          <div className="marquee-track opacity-70">
            {["Samsung TV+", "Xumo", "Wurl", "FuboTV", "SlingTV", "Roku", "Zee5", "Revolt", "HSN", "Digicel", "Tigo", "Frequency", "MegaCable"].concat(["Samsung TV+", "Xumo", "Wurl", "FuboTV", "SlingTV", "Roku", "Zee5", "Revolt"]).map((c, i) => (
              <div key={i} className="font-display font-bold text-2xl text-zinc-500 whitespace-nowrap tracking-tight">{c}</div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Alternating deep-dive feature blocks (Playout / OTT / Ad Insertion) ---------- */}
      <section className="py-24 px-6 border-t border-white/5" data-testid="deep-features">
        <div className="max-w-7xl mx-auto space-y-24">
          {[
            {
              tag: "MOXYPLAY Playout",
              title: "24×7 linear playout, without the play-out room.",
              body:
                "Automated playlist, live-source switching, SCTE-35 markers, warm-standby engines and 14 layers of live graphics — all driven from the browser. Origin your channel from anywhere; ship it everywhere.",
              points: [
                "Playlist + live cut-in with frame-accurate transitions",
                "Warm-standby engine per channel · sub-second cutover",
                "GStreamer 1.24 + NVENC pipeline · up to 3840×2160",
                "As-run logs to PostgreSQL and S3 · fully append-only",
              ],
              img: HERO_BG,
              blob: "blob-a",
            },
            {
              tag: "MOXYPLAY OTT",
              title: "One packaging pipeline — every OTT storefront.",
              body:
                "HLS + XMLTV EPG for Samsung TV+, Roku, Fire TV, Google TV, Web and STB. Multi-CDN egress, geo-restriction, DRM, and DVR window baked in.",
              points: [
                "HLS + LL-HLS · multi-bitrate packaged from a single origin",
                "Per-tenant CDN with multi-region failover",
                "XMLTV EPG generated from the live schedule",
                "DVR / catch-up window · seek-back on FAST storefronts",
              ],
              img: NET_BG,
              blob: "blob-b",
              reverse: true,
            },
            {
              tag: "MOXYPLAY Ad Insertion",
              title: "SSAI on SCTE-35 triggers. CSAI when it counts.",
              body:
                "AI-proposed break points, frame-accurate SCTE-35 insertion, dynamic creative substitution across CSAI and SSAI. Ad decisioning integrates with your existing ad server.",
              points: [
                "Frame-accurate SCTE-35 (DATERANGE / EXT-X-CUE compliant)",
                "AI break-point proposals · operator confirms in one keystroke",
                "SSAI stitching from a single per-tenant manifest",
                "CSAI fallback with dynamic creative replacement",
              ],
              img: EDIT_BG,
              blob: "blob-c",
            },
          ].map(({ tag, title, body, points, img, blob, reverse }) => (
            <div key={tag} className="grid lg:grid-cols-2 gap-10 items-center">
              <div className={reverse ? "lg:order-2" : ""}>
                <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-300">{tag}</div>
                <h3 className="mt-2 font-display font-black text-3xl sm:text-4xl tracking-tighter leading-[1.05]">{title}</h3>
                <p className="mt-4 text-muted-foreground leading-relaxed max-w-lg">{body}</p>
                <ul className="mt-6 space-y-2.5 text-sm">
                  {points.map((p) => (
                    <li key={p} className="flex items-start gap-3">
                      <CircleDot className="w-4 h-4 text-fuchsia-400 mt-0.5 shrink-0" />
                      <span className="text-foreground/85">{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className={`relative ${reverse ? "lg:order-1" : ""}`}>
                <div className={`relative aspect-[4/3] overflow-hidden ${blob}`} style={{ boxShadow: "0 30px 80px -30px rgba(139,92,246,0.55)" }}>
                  <img src={img} alt={tag} className="w-full h-full object-cover" />
                  <div className="absolute inset-0" style={{ background: "linear-gradient(160deg, rgba(139,92,246,0.35), rgba(34,211,238,0.12) 45%, transparent 75%)" }} />
                </div>
                <div className="absolute -bottom-4 -right-4 hidden md:block">
                  <div className="panel-hero rounded-2xl px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-cyan-300">
                    {tag}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ---------- Our Products — 6-card irregular blob grid ---------- */}
      <section className="py-24 px-6 border-t border-white/5" data-testid="our-products">
        <div className="max-w-7xl mx-auto">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">§ Our Products</div>
          <div className="flex items-end justify-between flex-wrap gap-4 mt-1">
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter">One platform. Six shipping products.</h2>
            <p className="text-sm text-muted-foreground max-w-md">Deploy them together, or slot any one into your existing stack. Every module is API-first.</p>
          </div>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Radio, title: "Playout Engine", desc: "Automated + live playlist with warm-standby.", blob: "blob-a", grad: "from-fuchsia-500/25 to-purple-500/10" },
              { icon: Layers, title: "Graphics Compositor", desc: "14-layer CEF + APNG stack, timeline-driven.", blob: "blob-b", grad: "from-purple-500/25 to-blue-500/10" },
              { icon: CircleDot, title: "Ad Insertion", desc: "SSAI + CSAI on SCTE-35 triggers.", blob: "blob-c", grad: "from-cyan-500/25 to-emerald-500/10" },
              { icon: Globe2, title: "OTT Delivery", desc: "HLS + LL-HLS + XMLTV EPG · multi-CDN.", blob: "blob-d", grad: "from-blue-500/25 to-cyan-500/10" },
              { icon: Sparkles, title: "AI Studio", desc: "Break detection + metadata authoring.", blob: "blob-e", grad: "from-pink-500/25 to-fuchsia-500/10" },
              { icon: Users, title: "Interactive Layer", desc: "Live polls, votes, and viewer overlays.", blob: "blob-f", grad: "from-emerald-500/25 to-cyan-500/10" },
            ].map(({ icon: Icon, title, desc, blob, grad }) => (
              <div key={title} className="relative group" data-testid={`product-${title.toLowerCase().replace(/\s+/g,'-')}`}>
                <div className={`absolute -inset-3 ${blob} bg-gradient-to-br ${grad} opacity-70 blur-2xl group-hover:opacity-100 transition-opacity`} />
                <div className={`relative ${blob} panel-hero p-6 min-h-[240px] flex flex-col justify-between`}>
                  <div>
                    <Icon className="w-7 h-7 text-fuchsia-300 mb-4" strokeWidth={1.5} />
                    <div className="font-display font-bold text-xl leading-tight">{title}</div>
                    <div className="text-sm text-muted-foreground mt-2 leading-relaxed">{desc}</div>
                  </div>
                  <div className="mt-6 text-[10px] font-mono uppercase tracking-widest text-cyan-300 opacity-0 group-hover:opacity-100 transition-opacity">
                    Learn more →
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Services grid × 6 ---------- */}
      <section className="py-20 px-6 border-t border-white/5" data-testid="services-grid">
        <div className="max-w-7xl mx-auto">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">§ Services</div>
          <h2 className="mt-1 font-display font-black text-3xl sm:text-4xl tracking-tighter">Managed and self-serve — you pick.</h2>
          <p className="mt-3 text-muted-foreground max-w-2xl">From white-glove channel onboarding to a self-serve sandbox, our services team backs every deployment.</p>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { title: "Channel Onboarding", desc: "Full playlist and graphics build-out on your assets, delivered in under a week.", tag: "White-glove" },
              { title: "Live Event Production", desc: "Dedicated engineer running SCTE-35, graphics and multi-cam switching for tent-pole events.", tag: "Live-ops" },
              { title: "OTT Packaging", desc: "One-click packaging for Samsung TV+, Roku, Fire TV, Google TV, and web STB storefronts.", tag: "Delivery" },
              { title: "24×7 NOC & SRE", desc: "SRE team monitoring engine health, CDN egress, and alerting into your Slack / PagerDuty.", tag: "Ops" },
              { title: "Migration", desc: "Zero-downtime migration from legacy playout systems (Grass Valley, Pebble, TAG, etc.).", tag: "Migration" },
              { title: "Custom Integrations", desc: "Ad-server, EPG, DRM, DAI, and monetisation SDKs stitched into your workflow.", tag: "Integrate" },
            ].map((s) => (
              <div key={s.title} className="panel rounded-2xl p-5 hover:-translate-y-0.5 transition-transform" data-testid={`service-${s.title.toLowerCase().replace(/\s+/g,'-')}`}>
                <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-300">{s.tag}</div>
                <div className="mt-2 font-display font-bold text-lg leading-tight">{s.title}</div>
                <div className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{s.desc}</div>
                <div className="mt-4 text-[10px] font-mono uppercase tracking-widest text-fuchsia-300">
                  Talk to services →
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Awards / Testimonial cards ---------- */}
      <section className="py-24 px-6 border-t border-white/5" data-testid="awards-testimonials">
        <div className="max-w-7xl mx-auto">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">§ Recognition</div>
          <h2 className="mt-1 font-display font-black text-3xl sm:text-4xl tracking-tighter">Awarded, benchmarked, on-air.</h2>

          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { year: "2025", label: "NAB Product of the Year · Cloud Playout" },
              { year: "2025", label: "IBC Best AI in Media · Break Detection" },
              { year: "2024", label: "Streaming Media 100" },
              { year: "2024", label: "TV Tech Green Award · Efficient Playout" },
            ].map((a) => (
              <div key={a.label} className="gradient-stroke rounded-2xl p-5" data-testid={`award-${a.year}-${a.label.toLowerCase().replace(/\W+/g,'-').slice(0,20)}`}>
                <div className="font-mono text-[10px] uppercase tracking-widest text-cyan-300">{a.year}</div>
                <div className="mt-2 font-display font-bold text-sm leading-tight">{a.label}</div>
              </div>
            ))}
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              {
                quote: "We cut our per-channel opex by 62% and shipped four new FAST channels the quarter we switched.",
                who: "Anaïs Moreau",
                role: "Head of Distribution",
                org: "Aurora Networks",
              },
              {
                quote: "The AI break-point proposals are wild — our editors approve them and move on. It used to take a whole shift.",
                who: "Rajiv Menon",
                role: "Post Supervisor",
                org: "Skyline Sports",
              },
              {
                quote: "Sub-second failover finally works the way the sales decks promised, five vendors deep in this space.",
                who: "Selin Yılmaz",
                role: "Head of Broadcast Ops",
                org: "Meridian TV Group",
              },
            ].map((t, i) => (
              <figure key={i} className="panel-hero rounded-2xl p-6 flex flex-col" data-testid={`testimonial-${i}`}>
                <div className="text-fuchsia-300 font-display font-black text-4xl leading-none">“</div>
                <blockquote className="mt-3 text-foreground/90 text-[15px] leading-relaxed flex-1">
                  {t.quote}
                </blockquote>
                <figcaption className="mt-5 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center font-display font-black text-sm text-white" style={{ background: "var(--brand-gradient)" }}>
                    {t.who.split(" ").map((s) => s[0]).slice(0,2).join("")}
                  </div>
                  <div>
                    <div className="font-display font-bold text-sm leading-tight">{t.who}</div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mt-0.5">
                      {t.role} · {t.org}
                    </div>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-24 px-6 border-t border-white/5">
        <div className="max-w-4xl mx-auto text-center panel-hero p-12">
          <p className="text-[10px] font-mono uppercase tracking-widest text-cyan-300">§ Ready?</p>
          <h2 className="font-display font-bold text-4xl sm:text-5xl mt-2 tracking-tighter">
            Launch broadcast-quality linear channels <span className="text-gradient">now</span>.
          </h2>
          <p className="text-zinc-400 mt-4">Signed pre-configured demo tenant — see MOXYPLAY running on your own channels.</p>
          <a href={BOOK_DEMO_URL} data-testid="footer-cta-btn">
            <Button className="tenant-btn rounded-full mt-8 h-12 px-8" size="lg">
              Book demo <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </a>
        </div>
      </section>

      <footer className="border-t border-white/5 py-8 px-6 text-center text-xs text-zinc-500 font-mono">
        © 2026 MOXYPLAY — a white-label FAST playout platform.
      </footer>
    </div>
  );
}
